import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Anchor, LogOut } from "lucide-react";
import NotificationsPopover from "@/components/NotificationsPopover";
import EnablePushBanner from "@/components/EnablePushBanner";

export default function AppShell({ items, children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const onLogout = async () => {
    await logout();
    nav("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex aamg-portal-bg relative overflow-hidden">
      {/* Animated mesh blobs (background canvas) */}
      <div className="aamg-portal-blob aamg-portal-blob-1" />
      <div className="aamg-portal-blob aamg-portal-blob-2" />
      <div className="aamg-portal-blob aamg-portal-blob-3" />
      <div className="absolute inset-0 aamg-grid-overlay opacity-[0.04] pointer-events-none" />
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex w-64 shrink-0 bg-white border-r border-slate-200 flex-col relative z-10" data-testid="app-sidebar">
        <div className="px-5 py-6 bg-[#0a1628] border-b border-slate-800">
          <img src="/brand/aamg-logo.png" alt="AAMG — Anesthesia Associates Medical Group" className="h-11 w-auto aamg-logo-glow" />
          <div className="mt-2.5 text-[10px] uppercase tracking-[0.18em] text-[#339999] font-semibold">Pre-Op Portal</div>
          <div className="text-[10px] italic text-[#E0A96D] mt-0.5">Compassionate Care. Clinical Excellence.</div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {items.map((it) => (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.end}
              data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-[#339999] text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <it.icon className="h-4 w-4" />
              {it.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-3 border-t border-slate-200">
          <div className="flex items-center gap-3 px-2 py-2">
            <Avatar className="h-9 w-9">
              {user?.picture ? <AvatarImage src={user.picture} /> : null}
              <AvatarFallback className="bg-[#E0A96D]/30 text-[#339999] text-sm font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-slate-900 truncate">{user?.name}</div>
              <div className="text-xs text-slate-500 truncate capitalize">{user?.role}</div>
            </div>
          </div>
          <Button onClick={onLogout} variant="ghost" className="w-full justify-start text-slate-600 mt-1" data-testid="logout-btn">
            <LogOut className="h-4 w-4 mr-2" /> Sign out
          </Button>
        </div>
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0 relative z-10">
        <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-xl border-b border-slate-200">
          <div className="md:hidden bg-[#0a1628] flex items-center justify-between px-4 py-3">
            <img src="/brand/aamg-logo.png" alt="AAMG — Anesthesia Associates Medical Group" className="h-8 w-auto aamg-logo-glow" />
            <div className="flex items-center gap-2">
              <NotificationsPopover />
              <Button onClick={onLogout} variant="ghost" size="sm" className="text-white hover:text-white hover:bg-white/10" data-testid="logout-btn-mobile">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="hidden md:flex items-center justify-between px-4 md:px-8 py-3">
            <div className="text-sm text-slate-500">
              {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </div>
            <div className="flex items-center gap-2">
              <NotificationsPopover />
            </div>
          </div>
          {/* Mobile nav */}
          <div className="md:hidden flex overflow-x-auto gap-1 px-3 py-2 border-t border-slate-100">
            {items.map((it) => (
              <NavLink key={it.to} to={it.to} end={it.end}
                className={({ isActive }) =>
                  `flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs whitespace-nowrap ${
                    isActive ? "bg-[#339999] text-white" : "text-slate-600 bg-slate-100"
                  }`
                }>
                <it.icon className="h-3.5 w-3.5" /> {it.label}
              </NavLink>
            ))}
          </div>
        </header>
        <main className="flex-1 p-4 md:p-8 max-w-[1400px] w-full mx-auto min-w-0 overflow-x-hidden">
          <EnablePushBanner />
          {children}
        </main>
      </div>
    </div>
  );
}
